import { MenuItem, CategoryDto, OrderItem } from '../types';

export interface SuggestionHistory {
  viewedProducts: {
    [productId: string]: {
      count: number;
      lastViewedAt: string;
    };
  };
  addedProducts: {
    [productId: string]: {
      count: number;
      lastAddedAt: string;
    };
  };
  purchasedProducts: {
    [productId: string]: {
      count: number;
      lastPurchasedAt: string;
    };
  };
}

const HISTORY_KEY_PREFIX = 'leo_delivery_suggestion_history_';

export const getHistory = (merchantId: string): SuggestionHistory => {
  const key = `${HISTORY_KEY_PREFIX}${merchantId}`;
  const saved = localStorage.getItem(key);
  if (saved) {
    try {
      return JSON.parse(saved);
    } catch (e) {
      console.error('Failed to parse suggestion history', e);
    }
  }
  return {
    viewedProducts: {},
    addedProducts: {},
    purchasedProducts: {},
  };
};

export const saveHistory = (merchantId: string, history: SuggestionHistory) => {
  const key = `${HISTORY_KEY_PREFIX}${merchantId}`;
  localStorage.setItem(key, JSON.stringify(history));
};

export const trackProductViewed = (merchantId: string, productId: string) => {
  if (!merchantId) return;
  const history = getHistory(merchantId);
  const view = history.viewedProducts[productId] || { count: 0, lastViewedAt: '' };
  history.viewedProducts[productId] = {
    count: view.count + 1,
    lastViewedAt: new Date().toISOString(),
  };
  saveHistory(merchantId, history);
};

export const trackProductAdded = (merchantId: string, productId: string) => {
  if (!merchantId) return;
  const history = getHistory(merchantId);
  const added = history.addedProducts[productId] || { count: 0, lastAddedAt: '' };
  history.addedProducts[productId] = {
    count: added.count + 1,
    lastAddedAt: new Date().toISOString(),
  };
  saveHistory(merchantId, history);
};

export const trackProductsPurchased = (merchantId: string, items: OrderItem[]) => {
  if (!merchantId) return;
  const history = getHistory(merchantId);
  items.forEach((item) => {
    const purchased = history.purchasedProducts[item.menuItemId] || { count: 0, lastPurchasedAt: '' };
    history.purchasedProducts[item.menuItemId] = {
      count: purchased.count + 1,
      lastPurchasedAt: new Date().toISOString(),
    };
  });
  saveHistory(merchantId, history);
};

const BEBIDA_KEYWORDS = ['bebida', 'bebidas', 'refrigerante', 'suco', 'água', 'agua', 'cerveja'];
const SOBREMESA_KEYWORDS = ['sobremesa', 'doces', 'doce', 'pudim', 'sorvete', 'brownie'];

const isBebida = (product: MenuItem, categoryName: string) => {
  const name = product.name.toLowerCase();
  const catName = categoryName.toLowerCase();
  const description = (product.description || '').toLowerCase();
  return (
    BEBIDA_KEYWORDS.some((kw) => name.includes(kw) || catName.includes(kw) || description.includes(kw)) ||
    product.drinkInfo?.alcoholic === true ||
    product.drinkInfo?.volumeMl !== undefined ||
    product.productType === 'DRINK'
  );
};

const isSobremesa = (product: MenuItem, categoryName: string) => {
  const name = product.name.toLowerCase();
  const catName = categoryName.toLowerCase();
  const description = (product.description || '').toLowerCase();
  return (
    SOBREMESA_KEYWORDS.some((kw) => name.includes(kw) || catName.includes(kw) || description.includes(kw)) ||
    product.productType === 'DESSERT'
  );
};

const COMPLEMENTARY_CATEGORIES: Record<string, string[]> = {
  'pizza': ['bebida', 'sobremesa', 'borda'],
  'hambúrguer': ['batata', 'bebida', 'sobremesa', 'combo'],
  'hamburguer': ['batata', 'bebida', 'sobremesa', 'combo'],
  'lanche': ['bebida', 'acompanhamento', 'batata'],
  'marmita': ['bebida', 'sobremesa', 'suco'],
  'açaí': ['complementos', 'bebida', 'adicionais'],
  'acai': ['complementos', 'bebida', 'adicionais'],
  'salgado': ['bebida', 'refrigerante'],
  'hot dog': ['bebida', 'batata', 'refrigerante'],
  'cachorro-quente': ['bebida', 'batata', 'refrigerante'],
  'cachorro quente': ['bebida', 'batata', 'refrigerante'],
};

export const getCartSuggestions = (
  cartItems: OrderItem[],
  categories: CategoryDto[],
  merchantId: string,
  maxSuggestions: number = 6
): MenuItem[] => {
  if (!merchantId) return [];

  const history = getHistory(merchantId);
  const cartProductIds = new Set(cartItems.map((item) => item.menuItemId));
  
  const allProducts: { product: MenuItem; categoryName: string }[] = [];
  categories.forEach((cat) => {
    const products = cat.products || (cat as any).items || [];
    products.forEach((prod: any) => {
      allProducts.push({ product: prod as MenuItem, categoryName: cat.name });
    });
  });

  const cartHasBebida = cartItems.some((item) => {
    const product = allProducts.find(p => p.product.id === item.menuItemId);
    return product ? isBebida(product.product, product.categoryName) : false;
  });

  const cartHasSobremesa = cartItems.some((item) => {
    const product = allProducts.find(p => p.product.id === item.menuItemId);
    return product ? isSobremesa(product.product, product.categoryName) : false;
  });

  const cartCategories = new Set<string>();
  cartItems.forEach(item => {
    const product = allProducts.find(p => p.product.id === item.menuItemId);
    if (product) {
      cartCategories.add(product.categoryName.toLowerCase());
    }
  });

  const scoredProducts = allProducts
    .filter(({ product }) => {
      // Regra 1: Não sugerir produto que já está no carrinho
      if (cartProductIds.has(product.id)) return false;

      // Regra 2: Não sugerir produto indisponível
      if (product.status === 'INACTIVE' || product.price <= 0) return false;
      
      // Se 'available' estiver definido, respeitar. Se não, assumir true se status for ativo
      if (product.available === false) return false;

      return true;
    })
    .map(({ product, categoryName }) => {
      let score = 0;

      // Priorizar bebidas (+50)
      if (!cartHasBebida && isBebida(product, categoryName)) {
        score += 50;
      }

      // Priorizar sobremesas (+40)
      if (!cartHasSobremesa && isSobremesa(product, categoryName)) {
        score += 40;
      }

      // Categorias complementares (+30)
      cartCategories.forEach(cartCat => {
        const complements = COMPLEMENTARY_CATEGORIES[cartCat] || [];
        if (complements.some(comp => categoryName.toLowerCase().includes(comp))) {
          score += 30;
        }
      });

      // Mesma categoria (+20)
      if (cartCategories.has(categoryName.toLowerCase())) {
        score += 20;
      }

      // Histórico: Adicionado antes (+15)
      if (history.addedProducts[product.id]) {
        score += 15;
      }

      // Histórico: Visualizado antes (+10)
      if (history.viewedProducts[product.id]) {
        score += 10;
      }

      // Destaque (+10)
      if (product.comercial?.highlight || product.featured) {
        score += 10;
      }

      // Base score for anyone else to ensure we have suggestions if possible
      // This is especially for cases where none of the above rules match
      score += 1;

      return { product, score };
    });

  return scoredProducts
    .sort((a, b) => b.score - a.score)
    .slice(0, maxSuggestions)
    .filter(p => p.score > 0)
    .map(p => p.product);
};

export const getProductTag = (product: MenuItem, categoryName: string): string | undefined => {
  if (isBebida(product, categoryName)) return 'Bebida';
  if (isSobremesa(product, categoryName)) return 'Sobremesa';
  if (product.comercial?.highlight || product.featured) return 'Destaque';
  return undefined;
};
